import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { usePageTitle } from '../contexts/PageContext';
import {
  AppBar,
  Avatar,
  Button,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Logout from '@mui/icons-material/Logout';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SystemUpdateAlt from '@mui/icons-material/SystemUpdateAlt';
import { SearchBar } from './SearchBar';

function SiteHeader() {
  const { user, logout, setSnackbar } = useWebAuthn();
  const navigate = useNavigate();
  const { title } = usePageTitle();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userMenuEl, setUserMenuEl] = useState<HTMLElement | null>(null);
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const [hamburgerMenuEl, setHamburgerMenuEl] = useState<HTMLElement | null>(
    null,
  );

  const handleuserMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuEl(e.currentTarget);
    setUserMenuOpen(!userMenuOpen);
  };

  const handleHamburgerMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHamburgerMenuEl(e.currentTarget);
    setHamburgerMenuOpen(!hamburgerMenuOpen);
  };

  const handleLogOutClick = () => {
    setUserMenuOpen(false);
    logout();
  };

  const handleCheckForUpdates = async () => {
    setUserMenuOpen(false);

    const upToDate = () =>
      setSnackbar({
        open: true,
        message: "You're up to date.",
        severity: 'info',
      });

    if (!('serviceWorker' in navigator)) {
      upToDate();
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        upToDate();
        return;
      }
      await registration.update();
      // If a new worker is pending, the 'sw-update-available' event +
      // UpdatesBanner (wired in App.tsx) handle it — stay silent here to avoid
      // double-handling. Otherwise, confirm the app is current.
      if (!registration.installing && !registration.waiting) {
        upToDate();
      }
    } catch {
      upToDate();
    }
  };

  const renderUserMenu = () => {
    return (
      <Menu
        anchorEl={userMenuEl}
        open={userMenuOpen}
        onClose={() => setUserMenuOpen(false)}
      >
        <MenuItem onClick={handleCheckForUpdates}>
          <ListItemIcon>
            <SystemUpdateAlt fontSize="small" />
          </ListItemIcon>
          Check for Updates
        </MenuItem>
        <MenuItem onClick={handleLogOutClick}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Log Out
        </MenuItem>{' '}
      </Menu>
    );
  };

  const renderHamburgerMenu = () => {
    return (
      <Menu
        anchorEl={hamburgerMenuEl}
        open={hamburgerMenuOpen}
        onClose={() => setHamburgerMenuOpen(false)}
      >
        <MenuItem
          onClick={() => {
            setHamburgerMenuOpen(false);
            navigate('/');
          }}
        >
          <ListItemIcon>
            <BookmarkIcon fontSize="small" />
          </ListItemIcon>
          Bookmarks
        </MenuItem>
        <MenuItem
          onClick={() => {
            setHamburgerMenuOpen(false);
            navigate('/admin');
          }}
        >
          <ListItemIcon>
            <AdminPanelSettings fontSize="small" />
          </ListItemIcon>
          Admin
        </MenuItem>
      </Menu>
    );
  };
  const renderUserContent = () => {
    if (user) {
      return (
        <>
          <Button onClick={handleuserMenuClick} color="inherit" sx={{ gap: 1 }}>
            <Typography
              sx={{ display: { xs: 'none', md: 'inline' } }}
              component="span"
            >
              {user.username}
            </Typography>
            <Avatar>{user.username.charAt(0).toUpperCase()}</Avatar>
          </Button>
        </>
      );
    } else return <>not logged in</>;
  };

  return (
    <Box>
      <AppBar position="static" color="primary" style={{ borderRadius: 0 }}>
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            sx={{ mr: { xs: 0.5, sm: 2 } }}
            onClick={handleHamburgerMenuClick}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'center' },
            }}
          >
            <SearchBar />
          </Box>
          {renderUserContent()}
        </Toolbar>
      </AppBar>
      {renderUserMenu()}
      {renderHamburgerMenu()}
    </Box>
  );
}

export default SiteHeader;
